import { client } from '@/services/api/axios.ts';
import { QueryClient, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

type AssignUsersToPlantResponse = {
  success: boolean;
  message?: string;
};

export const usePostAssignUsersToPlant = ({
  queryClient,
  onSuccess,
  plantId,
}: {
  onSuccess?: (data: AssignUsersToPlantResponse) => void;
  queryClient: QueryClient;
  plantId: string;
}) => {
  return useMutation({
    mutationFn: async ({ userIds, plantId }: { userIds: string[]; plantId: string }) => {
      const postPromise = client.post<AssignUsersToPlantResponse>(`/plants/${plantId}/users`, {
        userIds,
      });

      const { data } = await toast.promise(postPromise, {
        loading: 'Assigning users to plant...',
        success: 'Users assigned to plant successfully',
        error: (_) => {
          return 'Failed to assign users to plant';
        },
      });

      return data;
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onMutate: async ({ userIds }) => {
      const queryKey = ['plants', plantId, 'users'];

      await queryClient.cancelQueries({ queryKey });

      const previousUserIds = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, () => ({ userIds }));

      return { previousUserIds };
    },
    onError: (_, __, context) => {
      if (context?.previousUserIds) {
        queryClient.setQueryData(['plants', plantId, 'users'], context.previousUserIds);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['plants', plantId, 'users'] });
    },
  });
};
